import {
    type UnknownAction,
    type configureStore,
    combineReducers,
    type Slice,
    type ReducersMapObject,
    type Middleware,
    type Reducer,
} from '@reduxjs/toolkit';
import { type LooksLikeAnApi } from './ReduxHelpers.types';

const noOpReducer: Reducer = (state, _action: UnknownAction) => state;

// Store registered APIs for later access (e.g., resetting cache on user change)
let registeredApis: LooksLikeAnApi[] = [];

/**
 * Returns the list of RTK Query APIs registered with the store.
 * Used by resetAllUserSpecificState to reset API caches when user changes.
 */
export function getRegisteredApis(): LooksLikeAnApi[] {
    return registeredApis;
}

export function combineApisAndSlices({
    apis,
    slices,
    middleware,
}: {
    apis?: LooksLikeAnApi[];
    slices?: Slice[];
    middleware?: Middleware[];
}) {
    // Store APIs for later access (e.g., for resetting cache on user change)
    registeredApis = apis ?? [];
    const normalizedSlices = slices ?? [];

    const apiReducers: ReducersMapObject = registeredApis.reduce((acc, api) => {
        // Only add APIs with valid reducerPath to avoid "No reducer provided for key 'undefined'" error
        if (api.reducerPath) {
            acc[api.reducerPath] = api.reducer;
        }
        return acc;
    }, {} as ReducersMapObject);

    const reducers = normalizedSlices.reduce((acc, slice) => {
        // Only add slices with valid names to avoid "No reducer provided for key 'undefined'" error
        if (slice.name) {
            acc[slice.name] = slice.reducer;
        }
        return acc;
    }, apiReducers);

    const combinedMiddleware = registeredApis.reduce((acc, api) => {
        // Only add APIs with valid middleware to avoid "each middleware provided to configureStore must be a function" error
        if (api.middleware) {
            acc.push(api.middleware);
        }
        return acc;
    }, middleware ?? []);

    return {
        reducer: Object.keys(reducers).length > 0 ? combineReducers(reducers) : noOpReducer,
        middleware: getDefault =>
            getDefault({
                // serializableCheck is a built-in check that runs only in dev mode to warn you if you
                // put non-serializable objects into Redux. The tool has a built-in warning when it is running
                // slowly, and we started seeing that warning regularly and having the dev app run really slowly. The
                // warning says that if you're putting big objects into redux, then the check might be too slow and
                // you might have to disable it. We are intentionally putting big objects in Redux, so it seems like
                // we're just gonna have to live without this check.
                serializableCheck: false,
            }).concat(combinedMiddleware),
    } as Parameters<typeof configureStore>[0];
}
